import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// Clé versionnée : un panier écrit par un format antérieur est ignoré plutôt que
// migré. Un panier abandonné n'a aucune valeur, et du code de migration sur une
// donnée non fiable coûterait plus qu'il ne rapporte.
//
// Les Garanties Atelier (spec 2026-07-31) sont arrivées SANS changement de
// version, parce que l'ajout est purement additif et se dégrade proprement dans
// les deux sens : un panier écrit avant elles n'a pas la clé `warranties` et
// reste lu tel quel ; un panier écrit avec elles reste lisible par un code qui
// les ignore. Monter la version aurait vidé les paniers en cours au déploiement
// — un coût client réel pour aucun gain de sûreté. Ce qui protège de l'état
// bâtard, ce n'est pas le numéro de version, c'est `attachedWarranties`
// ci-dessous, qui refuse toute garantie ne couvrant pas une ligne du panier.
export const CART_STORAGE_KEY = 'mi_cart_v1';
const CART_VERSION = 1;

// Le reconditionné est une pièce unique d'atelier.
const UNIQUE_CONDITION = 'reconditionne';

const CartContext = createContext(null);

// Exporté pour que l'interface (fiche produit, page panier) désactive le « + »
// sur la même règle que celle qui borne réellement la quantité — sinon le bouton
// resterait actif et la quantité serait silencieusement écrêtée ici.
//
// Règle STRICTEMENT alignée sur `backend/services/cart_svc.py::_max_qty`, seul
// arbitre à la commande. Toute divergence se paie en 409 après coup, sur un
// panier que le client croyait valide :
//   - pièce unique (reconditionné) → 1, quel que soit le stock ;
//   - `stock` absent, `null` ou illisible → repli prudent à 1, le pipeline
//     catalogue n'a simplement pas encore propagé la valeur ;
//   - `stock` explicitement à 0 → 0, PAS de repli : une rupture annoncée ne
//     doit jamais rendre l'article vendable, même si `status` vaut encore
//     « available » (la propagation du stock est asynchrone).
export function maxQtyFor(product) {
  if ((product?.condition || UNIQUE_CONDITION) === UNIQUE_CONDITION) return 1;
  const raw = product?.stock;
  if (raw === null || raw === undefined || raw === '') return 1;
  const stock = Number(raw);
  if (!Number.isFinite(stock)) return 1;
  return Math.max(0, Math.trunc(stock));
}

// COUPLAGE des garanties aux lignes produit, tenu à un seul endroit.
//
// Une Garantie Atelier n'existe QUE rattachée à une ligne du panier : elle
// n'a pas de stock, pas de quantité propre, et son prix dépend du produit
// couvert. Retirer le produit retire donc sa garantie — c'est le seul couplage
// entre lignes de la spec, et il est appliqué ici, sur la voie d'écriture
// unique, plutôt que répété dans chaque mutation où on pourrait l'oublier.
//
// Le filtre écarte aussi les garanties inexploitables (durée ou montant absents,
// nuls, illisibles) : elles ne viennent que d'un stockage trafiqué ou d'un
// format futur, et une garantie dont on ne sait pas dire le prix ne doit jamais
// entrer dans un total. Jamais de montant à zéro, jamais d'état bâtard.
function attachedWarranties(warranties, items) {
  const covered = new Set((items || []).map((item) => item.product_id));
  return (Array.isArray(warranties) ? warranties : [])
    .filter((warranty) => warranty?.target_product_id
      && covered.has(warranty.target_product_id)
      && Number(warranty.years) > 0
      && Number(warranty.price) > 0)
    .map((warranty) => ({
      target_product_id: warranty.target_product_id,
      years: Number(warranty.years),
      price: Number(warranty.price),
      // Libellé du service (« Main-d'œuvre incluse pendant 1 an ») calculé par
      // le serveur. Jamais reconstruit ici : son texte exact est une décision
      // commerciale, pas une donnée qui se déduit d'un nombre d'années.
      service_label: warranty.service_label || '',
    }));
}

function readStoredCart() {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return { items: [], warranties: [] };
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CART_VERSION || !Array.isArray(parsed.items)) {
      return { items: [], warranties: [] };
    }
    const items = parsed.items.filter((item) => item?.product_id && Number(item.qty) > 0);
    return { items, warranties: attachedWarranties(parsed.warranties, items) };
  } catch {
    // localStorage indisponible (navigation privée, quota) ou contenu corrompu :
    // le panier repart vide, jamais d'erreur bloquante pour le client.
    return { items: [], warranties: [] };
  }
}

export function CartProvider({ children }) {
  // `items` et `warranties` sont tenus dans UN SEUL état : ils se commitent
  // ensemble, donc aucun rendu ne peut montrer une garantie rattachée à une
  // ligne déjà partie.
  const [cart, setCart] = useState(readStoredCart);
  const { items, warranties } = cart;

  // Miroir SYNCHRONE de `items`, nécessaire à deux titres :
  //   1. `addItem` doit répondre immédiatement si le panier a réellement changé.
  //      Un `setItems` fonctionnel ne le permet pas — React applique la mise à
  //      jour plus tard — et l'appelant afficherait une confirmation mensongère,
  //      « c'est dans votre panier » sur un article écrêté qui n'y est pas entré.
  //   2. Il garde les mutations STABLES (dépendances vides ou `[commit]`). Une
  //      implémentation à fermeture sur `items` changerait l'identité de
  //      `syncItems` à chaque rendu, et l'effet de relecture de la page panier,
  //      qui en dépend, reboucherait à l'infini.
  const cartRef = useRef(cart);

  // Voie d'écriture UNIQUE : toute mutation passe par ici, donc le miroir ne
  // peut pas diverger, y compris entre deux mutations du même événement (React
  // n'a alors encore rien recommité). C'est un CHOIX, pas une contrainte du
  // problème : laisser les autres mutations sur `setCart` fonctionnerait aussi
  // grâce à l'effet de rattrapage ci-dessous. Une seule voie se relit et se
  // vérifie mieux qu'un miroir plus une réparation.
  //
  // C'est aussi ce qui rend le couplage produit → garantie inratable : les
  // garanties repassent par `attachedWarranties` à CHAQUE écriture, donc aucune
  // mutation — présente ou future — ne peut laisser derrière elle une garantie
  // orpheline. `nextWarranties` omis = « inchangées », le cas de la plupart des
  // mutations produit.
  const commit = useCallback((nextItems, nextWarranties) => {
    const next = {
      items: nextItems,
      warranties: attachedWarranties(
        nextWarranties === undefined ? cartRef.current.warranties : nextWarranties,
        nextItems,
      ),
    };
    cartRef.current = next;
    setCart(next);
  }, []);

  // Filet : resynchronise le miroir à chaque rendu où le panier a bougé. Sans
  // objet tant que tout passe par `commit` — mais si une mutation future y
  // échappe, la divergence est effacée au rendu suivant au lieu de survivre. Un
  // invariant tenu par le code plutôt que par la discipline.
  useEffect(() => { cartRef.current = cart; }, [cart]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        CART_STORAGE_KEY,
        JSON.stringify({ version: CART_VERSION, items, warranties }),
      );
    } catch {
      // Quota dépassé ou stockage refusé : le panier reste utilisable en mémoire
      // pour la session en cours.
    }
  }, [items, warranties]);

  // Renvoie `true` si le panier a effectivement changé, `false` sinon — produit
  // inexploitable (pas de `product_id`), article en rupture, ou quantité déjà au
  // maximum. L'appelant DOIT s'en servir : sans ça, il ne peut que supposer, et
  // finit par féliciter le client d'un ajout qui n'a pas eu lieu.
  const addItem = useCallback((product, qty = 1) => {
    if (!product?.product_id) return false;
    const max = maxQtyFor(product);
    const current = cartRef.current.items;
    const existing = current.find((item) => item.product_id === product.product_id);
    const before = existing?.qty || 0;
    const clamped = Math.min(before + Math.max(1, Number(qty) || 1), max);
    if (clamped <= before) return false;
    // Instantané d'affichage uniquement : le serveur relit tout en base.
    const snapshot = {
      product_id: product.product_id,
      name: product.name || '',
      price: Number(product.price) || 0,
      image: product.image || product.image_url || null,
      availability: product.availability || 'stock_meteris',
      condition: product.condition || UNIQUE_CONDITION,
      stock: product.stock ?? null,
    };
    commit(existing
      ? current.map((item) => (item.product_id === product.product_id
        ? { ...snapshot, qty: clamped } : item))
      : [...current, { ...snapshot, qty: clamped }]);
    return true;
  }, [commit]);

  const updateQty = useCallback((productId, qty) => {
    const next = Number(qty) || 0;
    const current = cartRef.current.items;
    if (next <= 0) {
      commit(current.filter((item) => item.product_id !== productId));
      return;
    }
    commit(current
      .map((item) => (item.product_id === productId
        ? { ...item, qty: Math.min(next, maxQtyFor(item)) } : item))
      // Un stock retombé à zéro écrête à 0 : la ligne sort du panier plutôt
      // que d'y rester affichée, comptée pour rien et refusée à la commande.
      .filter((item) => item.qty > 0));
  }, [commit]);

  // Remet l'instantané d'affichage à jour depuis les fiches relues côté serveur.
  // `snapshotsById` est indexé par `product_id` et porte les champs du panier
  // (name, price, image, availability, condition, stock) — c'est à l'appelant de
  // traduire la forme de l'API, qui expose `id` et `images[]`.
  //
  // N'AJOUTE ni ne RETIRE aucune ligne : retirer un article est une décision du
  // client, jamais un effet de bord d'un rafraîchissement. Une ligne absente de
  // `snapshotsById` est laissée telle quelle. Seule la quantité est ré-écrêtée,
  // parce qu'un stock qui a baissé ne doit pas laisser une quantité impossible.
  //
  // La quantité a un PLANCHER À 1, jamais 0. Depuis que `maxQtyFor` peut valoir
  // 0 (rupture annoncée), l'écrêtage seul rendrait représentable une ligne à 0 :
  // affichée, comptée pour rien, réécrite en stockage, puis silencieusement
  // filtrée au chargement suivant — une disparition fantôme que personne n'a
  // demandée. Le plancher, lui, ne décide de rien : retirer une ligne reste une
  // décision du client. C'est `Cart.js` qui classe la rupture en écart
  // « bloquant », exclut la ligne des instantanés et propose de la retirer.
  const syncItems = useCallback((snapshotsById) => {
    if (!snapshotsById || Object.keys(snapshotsById).length === 0) return;
    commit(cartRef.current.items.map((item) => {
      const fresh = snapshotsById[item.product_id];
      if (!fresh) return item;
      const merged = {
        ...item,
        name: fresh.name || item.name,
        price: Number(fresh.price) || 0,
        image: fresh.image ?? item.image,
        availability: fresh.availability || item.availability,
        condition: fresh.condition || item.condition,
        stock: fresh.stock ?? null,
      };
      return { ...merged, qty: Math.max(1, Math.min(Number(item.qty) || 1, maxQtyFor(merged))) };
    }));
  }, [commit]);

  const removeItem = useCallback((productId) => {
    commit(cartRef.current.items.filter((item) => item.product_id !== productId));
  }, [commit]);

  // Pose, remplace ou retire la Garantie Atelier d'une ligne.
  //
  // `option` est l'option servie par le serveur — `{years, price, service_label}` —
  // ou `null` pour « Sans garantie ». UNE SEULE garantie par ligne : choisir
  // « 2 ans » après « 1 an » remplace, ne cumule pas ; le client change d'avis,
  // il ne commande pas deux forfaits.
  //
  // Renvoie `true` si le panier a changé, `false` sinon — produit absent du
  // panier (une garantie orpheline serait refusée par le serveur), durée ou
  // montant inexploitables. Comme `addItem`, cette réponse évite à l'appelant de
  // confirmer un choix qui n'a pas été enregistré.
  const setWarranty = useCallback((productId, option) => {
    const { items: current, warranties: currentWarranties } = cartRef.current;
    if (!productId || !current.some((item) => item.product_id === productId)) return false;
    const others = currentWarranties.filter(
      (warranty) => warranty.target_product_id !== productId,
    );
    if (!option) {
      if (others.length === currentWarranties.length) return false;
      commit(current, others);
      return true;
    }
    const years = Number(option.years);
    const price = Number(option.price);
    // Le serveur n'a pas su dire le prix : on ne pose rien plutôt que d'afficher
    // une garantie à zéro euro que personne ne facturera.
    if (!(years > 0) || !(price > 0)) return false;
    commit(current, [...others, {
      target_product_id: productId,
      years,
      price,
      service_label: option.service_label || '',
    }]);
    return true;
  }, [commit]);

  // Réconcilie les garanties choisies avec les options que le serveur propose
  // AUJOURD'HUI — `{product_id: [{years, price, service_label}]}`, tel que le rend
  // `POST /api/cart/warranty-options`. Le montant et le libellé sont repris de
  // la réponse ; une garantie dont la durée n'est plus proposée (barème coupé
  // dans les réglages, produit devenu inéligible, prix passé dans une autre
  // tranche sans cette durée) est RETIRÉE : on ne garde jamais une garantie
  // dont le serveur ne sait plus dire le prix.
  //
  // À n'appeler QUE sur une réponse effectivement reçue : un appel échoué ne
  // doit rien effacer. Sans garantie au panier, la fonction ne commite rien —
  // elle peut donc être appelée depuis un effet sans risque de boucle.
  const syncWarranties = useCallback((optionsByProduct) => {
    if (!optionsByProduct) return;
    const { items: current, warranties: currentWarranties } = cartRef.current;
    if (currentWarranties.length === 0) return;
    const next = [];
    currentWarranties.forEach((warranty) => {
      const options = optionsByProduct[warranty.target_product_id];
      const fresh = Array.isArray(options)
        ? options.find((option) => Number(option.years) === warranty.years)
        : null;
      if (!fresh) return;
      next.push({
        ...warranty,
        price: Number(fresh.price),
        service_label: fresh.service_label || warranty.service_label,
      });
    });
    commit(current, next);
  }, [commit]);

  const clear = useCallback(() => commit([], []), [commit]);

  const value = useMemo(() => {
    const qtyByProduct = new Map(items.map((item) => [item.product_id, Number(item.qty) || 0]));
    // La garantie suit l'UNITÉ : une ligne de trois PC garantis coûte trois fois
    // le montant du barème, exactement comme le facture `cart_svc.validate_cart`.
    const warrantyTotal = Math.round(warranties.reduce(
      (sum, warranty) => sum + warranty.price * (qtyByProduct.get(warranty.target_product_id) || 0),
      0) * 100) / 100;
    const itemsTotal = items.reduce(
      (sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 0), 0);
    return {
      items,
      warranties,
      // `count` compte les ARTICLES à retirer en boutique : une Garantie
      // Atelier n'en est pas un, elle n'entre donc pas dans la pastille.
      count: items.reduce((sum, item) => sum + (Number(item.qty) || 0), 0),
      warrantyTotal,
      total: Math.round((itemsTotal + warrantyTotal) * 100) / 100,
      addItem,
      updateQty,
      removeItem,
      syncItems,
      setWarranty,
      syncWarranties,
      clear,
    };
  }, [items, warranties, addItem, updateQty, removeItem, syncItems,
      setWarranty, syncWarranties, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart doit être utilisé dans un CartProvider');
  return context;
}
